import apiClient from '@/core/api/client';
import { API_ENDPOINTS } from '@/core/api/endpoints';
import { SchoolCreateRequest, SchoolResponse, SchoolListResponse, SchoolUsersResponse } from '../types';

/**
 * School API Service
 */
export const schoolApi = {
    /**
     * Create a new school (tenant)
     */
    createSchool: async (data: SchoolCreateRequest): Promise<SchoolResponse> => {
        try {
            const response = await apiClient.post<SchoolResponse>(API_ENDPOINTS.SCHOOLS.CREATE, data);
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || 'Failed to create school'
            };
        }
    },

    /**
     * Get all schools
     */
    getSchools: async (params?: { page?: number; page_size?: number }): Promise<SchoolListResponse> => {
        try {
            const response = await apiClient.get<SchoolListResponse>(API_ENDPOINTS.SCHOOLS.LIST, { params });
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || 'Failed to fetch schools'
            };
        }
    },

    /**
     * Get a single school's details by slug (public endpoint)
     */
    getSchoolBySlug: async (slug: string): Promise<SchoolResponse> => {
        try {
            const response = await apiClient.get<SchoolResponse>(API_ENDPOINTS.SCHOOLS.PUBLIC(slug));
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || 'Failed to fetch school details'
            };
        }
    },

    /**
     * Get user counts for a school by slug
     */
    getSchoolUsers: async (slug: string): Promise<SchoolUsersResponse> => {
        try {
            const response = await apiClient.get<SchoolUsersResponse>(API_ENDPOINTS.SCHOOLS.USERS(slug));
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || 'Failed to fetch school users'
            };
        }
    },

    /**
     * Delete a school by ID (Super Admin only)
     */
    deleteSchool: async (schoolId: string): Promise<{ success: boolean; message?: string; error?: string }> => {
        try {
            const response = await apiClient.delete(API_ENDPOINTS.SCHOOLS.DELETE(schoolId));
            return { success: true, message: response.data?.message || 'School deleted successfully' };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || 'Failed to delete school'
            };
        }
    },

    /**
     * Toggle school active/inactive status (Super Admin only)
     */
    toggleSchoolStatus: async (schoolId: string, isActive: boolean): Promise<{ success: boolean; message?: string; error?: string }> => {
        try {
            const response = await apiClient.patch(API_ENDPOINTS.SCHOOLS.TOGGLE_STATUS(schoolId), { is_active: isActive });
            return { success: true, message: response.data?.message || `School ${isActive ? 'activated' : 'deactivated'} successfully` };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || 'Failed to update school status'
            };
        }
    },
};
